import React, { useState, useEffect } from 'react';
import './NewReportModal.css';
import config from '../../config';
import AccessControl from '../../components/AccessControl';

const NewReportModal = ({ isOpen, onClose, surveys, reports = [], reportsFolders = [], defaultReportsFolder = [], onReportCreated }) => {
  const [reportTitle, setReportTitle] = useState('');
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [personaBrowserPath, setPersonaBrowserPath] = useState([]);
  const [personaFolders, setPersonaFolders] = useState([]);
  const [personaItems, setPersonaItems] = useState([]);
  const [personaLoading, setPersonaLoading] = useState(false);
  const [responsesPerQuestion, setResponsesPerQuestion] = useState(3);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [selectedFolderPath, setSelectedFolderPath] = useState(defaultReportsFolder);
  const [selectedAccess, setSelectedAccess] = useState(['All']);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (surveyDropdownOpen || modelDropdownOpen || folderDropdownOpen) {
      const handleClickOutside = (e) => {
        const wrapper = e.target.closest('.custom-dropdown-wrapper');
        if (!wrapper) {
          setSurveyDropdownOpen(false);
          setModelDropdownOpen(false);
          setFolderDropdownOpen(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [surveyDropdownOpen, modelDropdownOpen, folderDropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPersonaBrowserPath([]);
    fetchPersonaBrowser([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePersonaToggle = (personaId) => {
    setSelectedPersonaIds(prev =>
      prev.includes(personaId)
        ? prev.filter(id => id !== personaId)
        : [...prev, personaId]
    );
  };

  const arraysEqual = (a = [], b = []) => {
    return a.length === b.length && a.every((val, idx) => val === b[idx]);
  };

  const fetchPersonaBrowser = async (folderPath = []) => {
    setPersonaLoading(true);
    try {
      const folderPathParam = folderPath.join(',');
      const [personasRes, foldersRes] = await Promise.all([
        fetch(`${config.api.baseUrl}/synthetic-audience/personas?folder_path=${folderPathParam}`, {
          credentials: 'include'
        }),
        fetch(`${config.api.baseUrl}/synthetic-audience/folders?section=personas&folder_path=${folderPathParam}`, {
          credentials: 'include'
        })
      ]);

      if (!personasRes.ok || !foldersRes.ok) {
        throw new Error('Failed to load personas for this folder');
      }

      const personasData = await personasRes.json();
      const foldersData = await foldersRes.json();
      const currentLevelPersonas = (personasData.personas || []).filter((persona) =>
        arraysEqual(persona.folder_path || [], folderPath)
      );

      setPersonaItems(currentLevelPersonas);
      setPersonaFolders(foldersData.folders || []);
    } catch (err) {
      setError(err.message || 'Failed to load personas');
      setPersonaItems([]);
      setPersonaFolders([]);
    } finally {
      setPersonaLoading(false);
    }
  };

  const navigatePersonaFolder = (path) => {
    setPersonaBrowserPath(path);
    fetchPersonaBrowser(path);
  };

  const selectAllPersonas = () => {
    setSelectedPersonaIds((prev) => {
      const next = new Set(prev);
      personaItems.forEach((persona) => next.add(persona._id));
      return Array.from(next);
    });
  };

  const clearAllPersonas = () => {
    setSelectedPersonaIds([]);
  };

  const handleSurveyChange = (surveyId) => {
    setSelectedSurveyId(surveyId);
    setSurveyDropdownOpen(false);
  };

  const handleModelChange = (model) => {
    setSelectedModel(model);
    setModelDropdownOpen(false);
  };

  const handleFolderChange = (path) => {
    setSelectedFolderPath(path);
    setFolderDropdownOpen(false);
  };

  // Get all unique folder paths from folders (construct full paths recursively)
  const getAllFolderPaths = () => {
    const paths = new Set();

    // Helper to recursively build paths
    const buildPaths = (folders, currentPath = []) => {
      folders.forEach(folder => {
        const fullPath = [...currentPath, folder.name];
        paths.add(JSON.stringify(fullPath));

        // Find subfolders
        const subfolders = reportsFolders.filter(f =>
          JSON.stringify(f.folder_path) === JSON.stringify(fullPath)
        );
        if (subfolders.length > 0) {
          buildPaths(subfolders, fullPath);
        }
      });
    };

    // Start with root folders
    const rootFolders = reportsFolders.filter(f =>
      !f.folder_path || f.folder_path.length === 0
    );
    buildPaths(rootFolders);

    return Array.from(paths).map(str => JSON.parse(str));
  };

  const allReportFolders = getAllFolderPaths();

  const handleCreate = async () => {
    if (!selectedSurveyId) {
      setError('Please select a survey');
      return;
    }

    if (selectedPersonaIds.length === 0) {
      setError('Please select at least one persona');
      return;
    }

    // Validate responses per question
    const responsesNum = parseInt(responsesPerQuestion);
    if (isNaN(responsesNum) || responsesNum < 1) {
      setError('Responses per question must be a valid number greater than 0');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Generate default title if user left it blank
      let finalTitle = reportTitle.trim();
      if (!finalTitle) {
        const selectedSurvey = surveys.find(s => s._id === selectedSurveyId);
        if (selectedSurvey) {
          const now = new Date();
          const dateStr = now.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(/(\d+)\/(\d+)\/(\d+),/, '$3-$1-$2');
          finalTitle = `${selectedSurvey.title} - ${dateStr}`;
        }
      }

      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: finalTitle,
          survey_id: selectedSurveyId,
          selected_personas: selectedPersonaIds,
          responses_per_question: responsesNum,
          model: selectedModel,
          folder_path: selectedFolderPath,
          access: selectedAccess,
          execute_now: true
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create report');
      }

      const data = await res.json();

      // Reset form
      setReportTitle('');
      setSelectedSurveyId('');
      setSelectedPersonaIds([]);
      setResponsesPerQuestion(3);
      setSelectedModel('gpt-4o-mini');
      setSelectedFolderPath(defaultReportsFolder);
      setSelectedAccess(['All']);

      // Notify parent and close
      onReportCreated(data.report_id);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Report</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Report Title (optional)</label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              className="form-input"
              placeholder="Leave blank to auto-generate from survey name + date"
              disabled={creating}
            />
            <small className="form-hint">
              Custom name for this report (default: Survey Name - Date)
            </small>
          </div>

          <div className="form-group">
            <label>Select Survey *</label>
            <div className="custom-dropdown-wrapper">
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={() => !creating && setSurveyDropdownOpen(!surveyDropdownOpen)}
                disabled={creating}
              >
                <span className="custom-dropdown-text">
                  {selectedSurveyId
                    ? surveys.find(s => s._id === selectedSurveyId)?.title || 'Choose a survey...'
                    : 'Choose a survey...'}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {surveyDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {surveys.map((survey) => (
                    <button
                      key={survey._id}
                      type="button"
                      className={`custom-dropdown-option ${selectedSurveyId === survey._id ? 'active' : ''}`}
                      onClick={() => handleSurveyChange(survey._id)}
                    >
                      <div className="dropdown-option-content">
                        <span className="dropdown-option-title">{survey.title}</span>
                        <span className="dropdown-option-meta">
                          {survey.questions?.length || 0} questions
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>AI Model</label>
            <div className="custom-dropdown-wrapper">
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={() => !creating && setModelDropdownOpen(!modelDropdownOpen)}
                disabled={creating}
              >
                <span className="custom-dropdown-text">{selectedModel}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {modelDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {['gpt-5-mini', 'gpt-5-nano', 'gpt-5.2', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                    <button
                      key={model}
                      type="button"
                      className={`custom-dropdown-option ${selectedModel === model ? 'active' : ''}`}
                      onClick={() => handleModelChange(model)}
                    >
                      <div className="dropdown-option-content">
                        <span className="dropdown-option-title">{model}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <small className="form-hint">
              Choose the OpenAI model for generating responses
            </small>
          </div>

          <div className="form-group">
            <div className="persona-selection-header">
              <label>Select Personas ({selectedPersonaIds.length} selected)</label>
              <div className="persona-selection-actions">
                <button
                  type="button"
                  onClick={selectAllPersonas}
                  className="persona-select-btn"
                  disabled={creating}
                >
                  Select Visible
                </button>
                <button
                  type="button"
                  onClick={clearAllPersonas}
                  className="persona-select-btn"
                  disabled={creating}
                >
                  Clear All
                </button>
              </div>
            </div>
            <div className="persona-folder-breadcrumb">
              <button
                type="button"
                className={`persona-breadcrumb-link ${personaBrowserPath.length === 0 ? 'active' : ''}`}
                onClick={() => navigatePersonaFolder([])}
                disabled={creating || personaLoading}
              >
                Root
              </button>
              {personaBrowserPath.map((segment, idx) => (
                <React.Fragment key={`${segment}-${idx}`}>
                  <span className="persona-breadcrumb-separator">›</span>
                  <button
                    type="button"
                    className={`persona-breadcrumb-link ${idx === personaBrowserPath.length - 1 ? 'active' : ''}`}
                    onClick={() => navigatePersonaFolder(personaBrowserPath.slice(0, idx + 1))}
                    disabled={creating || personaLoading}
                  >
                    {segment}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="persona-selection">
              {personaLoading ? (
                <div className="no-personas">Loading personas...</div>
              ) : personaFolders.length === 0 && personaItems.length === 0 ? (
                <div className="no-personas">
                  No personas in this folder.
                </div>
              ) : (
                <>
                  {personaFolders.map((folder) => (
                    <button
                      key={folder._id}
                      type="button"
                      className="persona-folder-row"
                      onClick={() => navigatePersonaFolder([...personaBrowserPath, folder.name])}
                      disabled={creating}
                    >
                      <span className="persona-folder-icon" aria-hidden="true">Folder</span>
                      <span className="persona-folder-name">{folder.name}</span>
                    </button>
                  ))}
                  {personaItems.map((persona) => (
                    <label key={persona._id} className="persona-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPersonaIds.includes(persona._id)}
                        onChange={() => handlePersonaToggle(persona._id)}
                        disabled={creating}
                      />
                      <span>{persona.name}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Responses Per Question</label>
            <input
              type="text"
              value={responsesPerQuestion}
              onChange={(e) => setResponsesPerQuestion(e.target.value)}
              className="form-input"
              placeholder="e.g., 3"
              disabled={creating}
            />
            <small className="form-hint">
              Number of times each persona will answer each question (recommended: 3-5 for statistical variance)
            </small>
          </div>

          <div className="form-group">
            <label>Save to Folder</label>
            <div className="custom-dropdown-wrapper">
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={() => !creating && setFolderDropdownOpen(!folderDropdownOpen)}
                disabled={creating}
              >
                <span className="custom-dropdown-text">
                  {selectedFolderPath.length > 0 ? selectedFolderPath.join(' › ') : 'Root (No Folder)'}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {folderDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <button
                    type="button"
                    className={`custom-dropdown-option ${selectedFolderPath.length === 0 ? 'active' : ''}`}
                    onClick={() => handleFolderChange([])}
                  >
                    <div className="dropdown-option-content">
                      <span className="dropdown-option-title">Root (No Folder)</span>
                    </div>
                  </button>
                  {allReportFolders
                    .filter(path => path.length > 0)
                    .sort((a, b) => a.join('/').localeCompare(b.join('/')))
                    .map((path, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`custom-dropdown-option ${JSON.stringify(selectedFolderPath) === JSON.stringify(path) ? 'active' : ''}`}
                        onClick={() => handleFolderChange(path)}
                      >
                        <div className="dropdown-option-content">
                          <span className="dropdown-option-title">{path.join(' › ')}</span>
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <small className="form-hint">
              Choose which folder to save this report in
            </small>
          </div>

          <div className="form-group">
            <label>Report Access</label>
            <AccessControl value={selectedAccess} onChange={setSelectedAccess} />
            <small className="form-hint">
              Choose who can access this report and its results
            </small>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={creating}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? 'Creating...' : 'Create & Run Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewReportModal;
